/**
 * Role utility functions for formatting and displaying user roles
 */

import { ROLE_NAMES } from '../constants/roles.constants';

/**
 * Normalize a role string to a consistent format (lowercase with underscores)
 * This handles both old format (LYT_Admin) and new format (lyt_admin)
 * @param {string} role - Raw role string
 * @returns {string} Normalized role string (lowercase)
 */
export const normalizeRole = (role) => {
    if (!role) return '';
    return role.toLowerCase();
};

/**
 * Format a role string for display
 * Replaces underscores with spaces and capitalizes each word
 * @param {string} role - Raw role string (e.g., "LYT_Admin", "lyt_admin")
 * @returns {string} Formatted role name (e.g., "Lyt Admin")
 */
export const formatRole = (role) => {
    if (!role) return '-';
    
    return role
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

/**
 * Format a role for the Agency Team table, including a traffic viewer's
 * assigned jurisdictions when they are available.
 *
 * @param {string} role - Raw role string
 * @param {Array<Object|string>} jurisdictions - Assigned jurisdiction records or IDs
 * @returns {string} Formatted role with comma-separated jurisdiction labels
 */
export const formatRoleWithJurisdictions = (role, jurisdictions = []) => {
    const formattedRole = formatRole(role);

    if (normalizeRole(role) !== ROLE_NAMES.TRAFFIC_VIEWER || !Array.isArray(jurisdictions)) {
        return formattedRole;
    }

    const jurisdictionNames = [...new Set(
        jurisdictions
            .map((jurisdiction) => {
                if (typeof jurisdiction === 'string') {
                    return jurisdiction.trim();
                }

                return jurisdiction?.name?.trim() || jurisdiction?.id?.trim() || '';
            })
            .filter(Boolean)
    )];

    return jurisdictionNames.length > 0
        ? `${formattedRole}: ${jurisdictionNames.join(', ')}`
        : formattedRole;
};

/** Short labels for LYT Team Management (Lyt Admin, Lyt User). Keys are backend role_name values. */
const LYT_TEAM_ROLE_LABELS = Object.freeze({
    [ROLE_NAMES.LYT_ADMIN]: 'Lyt Admin',
    [ROLE_NAMES.LYT_EMPLOYEE]: 'Lyt User',
});

/**
 * Format a role for LYT Team Management UI (invite / edit / table filters).
 * @param {string} role - Raw role string (e.g. "lyt_admin", "lyt_employee")
 * @returns {string} "Admin", "User", or {@link formatRole} fallback
 */
export const formatLytTeamRole = (role) => {
    if (!role) return '-';
    const normalized = normalizeRole(role);
    return LYT_TEAM_ROLE_LABELS[normalized] ?? formatRole(role);
};

/**
 * Get display text for a role, with fallback for empty/null values
 * @param {string} role - Raw role string
 * @param {string} fallback - Fallback text if role is empty (default: '-')
 * @returns {string} Formatted role name or fallback
 */
export const getRoleDisplayText = (role, fallback = '-') => {
    if (!role) return fallback;
    return formatRole(role);
};
